import React from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { bsiInternetBankApi } from '@/lib/banks/bsi/api';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

const BsiIbanInquiryTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [accountNumber, setAccountNumber] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<{ iban: string | null; account_name: string | null; account_number: string | null } | null>(null);

  const handleSubmit = async () => {
    if (!accountNumber.trim()) {
      onMessage({ type: 'error', text: 'شماره حساب را وارد کنید.' });
      return;
    }
    setLoading(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.inquiryIban({ account_number: accountNumber.trim() });
      setResult(res.data);
      onMessage({ type: 'success', text: res.message || 'استعلام شبا انجام شد.' });
    } catch (e) {
      const text = e instanceof Error ? e.message : 'استعلام شبا ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="lg:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Search className="h-4 w-4" />
          استعلام شبا
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!sessionValid ? (
          <p className="text-sm text-amber-700">برای استعلام شبا ابتدا وارد اینترنت‌بانک شوید.</p>
        ) : (
          <>
            <div className="space-y-2">
              <Label>شماره حساب</Label>
              <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} dir="ltr" className="font-mono" placeholder="011..." />
            </div>
            <Button type="button" onClick={() => void handleSubmit()} disabled={loading}>
              {loading ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
              استعلام
            </Button>
            {result && (
              <div className="rounded-md border bg-slate-50 px-3 py-3 text-sm space-y-2">
                <p>شماره حساب: <span className="font-mono">{result.account_number || accountNumber}</span></p>
                <p>شماره شبا: <span className="font-mono font-semibold">{result.iban || '—'}</span></p>
                <p>نام حساب: {result.account_name || '—'}</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default BsiIbanInquiryTab;
